import { from, Observable, of, OperatorFunction, pipe } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { fromFetch } from 'rxjs/fetch';

import {
  AccountBalanceInfoResult,
  AccountBalanceResult,
  AccountInfoResult,
  ActivityExportDeployStatusesResult,
  ActivityExportEntitlementNFTEntryType,
  ActivityExportRecentRequest,
  ActivityExportRequestResult,
  ActivityExportRequestType,
  AssociatedKeyRelatedToAccount,
  BlockResult,
  ContractResult,
  CsprNameInfoResult,
  CurrencyFiatResult,
  CurrencyRateResult,
  DailyDeployNumberResult,
  DelegatorResult,
  DelegatorRewardResult,
  DeployContractPackageResult,
  DeployResult,
  FriendlyMarketTokenPairResult,
  FTBalanceTokenResult,
  FTTokenOwnerResult,
  FTTransactionResult,
  GetDeployResult,
  MapDailyDeployNumberResult,
  MapDataResponseValidators,
  MapMetadata,
  MapPaginatedBlock,
  MapPaginatedBlocks,
  MapPaginatedDeploy,
  MapPaginatedDeploys,
  MapPaginatedValidators,
  MapTokensMetadataAsList,
  NetworkStats,
  NFTTokenActivityResult,
  NFTTokenMetadataEntry,
  NFTTokenOwnerResult,
  NFTTokenResult,
  RawDeployJSON,
  Response,
  StatusResult,
  SupplyResult,
  TotalRewardsResult,
  TransferResult,
  ValidatorMetricsResult,
  ValidatorResult,
  ValidatorRewardResult,
  RawTransactionJSON,
  URefDelegationResult,
} from '@block-explorer/entities';
import { OrderDirection } from '@block-explorer/ui';

export type DataResponse<T = any> = {
  data: T;
  error?: { message: string };
};

export type RpcResponse<T = any> = {
  result: T;
  error?: { code: number; message: string };
};

export type PaginatedResponse<T = any> = {
  data: T[];
  item_count: number;
  page_count: number;
};

export type RequestParams = Record<
  string,
  string | boolean | string[] | number | null | undefined
>;

export type QueryRequestParams = RequestParams | undefined;

export type PostRequestParams = Record<
  string,
  | Record<string, string | number>
  | string
  | boolean
  | string[]
  | number
  | number[]
  | null
  | undefined
>;

export type PaginationParams<T = any> = {
  page: number;
  page_size: number;
  order_by: string | undefined;
  order_direction: OrderDirection;
};

export const fromFetchResponseHandler: OperatorFunction<any, any> = switchMap(
  (res) => {
    return res.json();
  }
);

export const fetchResponseHandler = (res) => {
  return res.json();
};

export const globalErrorHandler = catchError((error) => {
  console.error(error);
  return of(error);
});

const RPC_URL = config.cspr_rpc_url;
const CSPR_LIVE_API_URL = config.cspr_live_api_url;

const replacePaginatedParams = (url: string, params: RequestParams): string => {
  const urlHasQuery = url.includes('?');
  const concatSign = urlHasQuery ? '&' : '?';
  const pageLimitQuery = 'page_size=:page_size';

  return replaceParams(
    `${url}${concatSign}page=:page&${pageLimitQuery}&order_by=:order_by&order_direction=:order_direction`,
    params
  );
};

const replaceParams = (url: string, params: RequestParams): string => {
  return Object.entries(params).reduce((_url, [key, value]) => {
    // append new url param when url param key not in the URL
    if (!_url.includes(key)) {
      const urlHasQuery = _url.includes('?');
      const concatSign = urlHasQuery ? '&' : '?';

      return _url.concat(`${concatSign}${key}=${value}`);
    }

    // remove placeholder when value null
    if (value == null) {
      _url = _url.replace(`&${key}=:${key}`, '');
      return _url.replace(`?${key}=:${key}`, '');
    }

    // replace placeholder with value
    return _url.replace(`:${key}`, value.toString());
  }, url);
};

// one item always returned use get
// one item by search term returned find
export const eventStoreApiClient = (apiUrl: string) => {
  return {
    createPaginatedGet:
      <Entity extends any, Params extends RequestParams = RequestParams>(
        resource: string
      ) =>
      (
        params: Partial<PaginationParams<Entity>> & Params
      ): Observable<PaginatedResponse<Entity>> =>
        fromFetch(`${apiUrl}${replacePaginatedParams(resource, params)}`).pipe(
          fromFetchResponseHandler,
          catchError(globalErrorHandler)
        ),

    createRpcGet:
      <Entity extends any, Params extends RequestParams>(resource: string) =>
      (params: Params): Promise<Entity> => {
        return fetch(`${apiUrl}${replaceParams(resource, params)}`).then(
          fetchResponseHandler
        );
      },

    createGet:
      <Entity extends any, Params extends RequestParams>(resource: string) =>
      (params: Params): Observable<Entity> => {
        return fromFetch(`${apiUrl}${replaceParams(resource, params)}`).pipe(
          fromFetchResponseHandler,
          catchError(globalErrorHandler)
        );
      },

    createPaginatedPost:
      <Entity extends any, Params extends RequestParams = RequestParams>(
        resource: string
      ) =>
      ({
        page_size,
        order_by,
        order_direction,
        page,
        includes,
        ...params
      }: Partial<PaginationParams<Entity>> & Params): Observable<
        PaginatedResponse<Entity>
      > =>
        fromFetch(
          `${apiUrl}${replacePaginatedParams(resource, {
            page_size,
            order_by,
            order_direction,
            page,
            includes,
          })}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(params),
          }
        ).pipe(fromFetchResponseHandler, catchError(globalErrorHandler)),
    createPost:
      <
        Entity extends any,
        Params extends PostRequestParams,
        PostQueryRequestParams extends QueryRequestParams = undefined,
      >(
        resource: string
      ) =>
      (
        params: Params,
        queryRequestParams?: PostQueryRequestParams
      ): Observable<Entity> => {
        const resourceQuery = queryRequestParams
          ? replaceParams(resource, queryRequestParams)
          : resource;
        return fromFetch(`${apiUrl}${resourceQuery}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(params),
        }).pipe(fromFetchResponseHandler, catchError(globalErrorHandler));
      },
  };
};

export const baseSubscription =
  <Entity extends any, Params extends PostRequestParams>(
    clientRequest: (params: Params) => Observable<Entity> | Promise<Entity>
  ): ((params: Params) => Observable<Entity>) =>
  (params: Params) =>
    from(clientRequest(params)).pipe(catchError(globalErrorHandler));

export const postBaseSubscription =
  <
    Entity extends any,
    Params extends PostRequestParams,
    PostQueryRequestParams = undefined,
  >(
    clientRequest: (
      params: Params,
      queryRequestParams?: PostQueryRequestParams
    ) => Observable<Entity> | Promise<Entity>
  ): ((
    params: Params,
    queryRequestParams?: PostQueryRequestParams
  ) => Observable<Entity>) =>
  (params: Params, queryRequestParams?: PostQueryRequestParams) =>
    from(clientRequest(params, queryRequestParams)).pipe(
      catchError(globalErrorHandler)
    );

/**
 * Subscriptions
 */

/** BLOCK */

const getPaginatedBlocks =
  eventStoreApiClient(CSPR_LIVE_API_URL).createPaginatedGet<BlockResult>(
    `/blocks`
  );
export const getPaginatedBlocksSubscription = pipe(
  baseSubscription(getPaginatedBlocks),
  map(MapPaginatedBlocks)
);

const getBlock = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<BlockResult>,
  { hash: string }
>(`/blocks/:hash`);
export const getBlockSubscription = pipe(
  baseSubscription(getBlock),
  map(MapPaginatedBlock)
);

const getPaginatedBlockDeploys = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<DeployResult, { hash: string; includes?: string }>(
  `/blocks/:hash/deploys`
);

export const getPaginatedBlockDeploysSubscription = pipe(
  baseSubscription(getPaginatedBlockDeploys),
  map(MapPaginatedDeploys)
);

const getPaginatedBlockTransfers = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<TransferResult, { hash: string }>(
  `/blocks/:hash/transfers`
);
export const getPaginatedBlockTransfersSubscription = baseSubscription(
  getPaginatedBlockTransfers
);

/** DEPLOY */

const getPaginatedDeploys = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  DeployResult,
  {
    includes?: string[];
    contract_package_hash?: string;
  }
>(`/deploys`);
export const getPaginatedDeploysSubscription = pipe(
  baseSubscription(getPaginatedDeploys),
  map(MapPaginatedDeploys)
);

const getDeploy = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<DeployResult>,
  {
    hash: string;
    includes?: string[];
  }
>(`/deploys/:hash`);
export const getDeploySubscription = pipe(
  baseSubscription(getDeploy),
  map(MapPaginatedDeploy)
);

const getPaginatedDeployTransfers = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  TransferResult,
  {
    deploy_hash: string;
    includes?: string;
  }
>(`/deploys/:deploy_hash/transfers`);
export const getPaginatedDeployTransfersSubscription = baseSubscription(
  getPaginatedDeployTransfers
);

const getPaginatedDailyDeployNumbers = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<
  Response<DailyDeployNumberResult>,
  { from: string; to: string; order_direction: string; order_by: string }
>(`/daily-deploy-numbers`);
export const getPaginatedDailyDeployNumbersSubscription = pipe(
  baseSubscription(getPaginatedDailyDeployNumbers),
  map(MapDailyDeployNumberResult)
);

const getDeployRawData = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<GetDeployResult>,
  { hash: string }
>(`/raw-transactions/:hash`);
export const getDeployRawDataSubscription = baseSubscription(getDeployRawData);

/** ACCOUNT */

const getPaginatedAccountDeploys = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<DeployResult, { publicKey: string; includes?: string }>(
  `/accounts/:publicKey/deploys`
);
export const getPaginatedAccountDeploysSubscription = pipe(
  baseSubscription(getPaginatedAccountDeploys),
  map(MapPaginatedDeploys)
);

const getPaginatedAccountTransfers = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  TransferResult,
  {
    hash: string;
    includes?: string;
  }
>(`/accounts/:hash/transfers`);
export const getPaginatedAccountTransfersSubscription = baseSubscription(
  getPaginatedAccountTransfers
);

const getPaginatedAccountDelegations = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  DelegatorResult,
  {
    publicKey: string;
    includes?: string;
    page_size?: number;
  }
>(`/accounts/:publicKey/delegations`);
export const getPaginatedAccountDelegationsSubscription = baseSubscription(
  getPaginatedAccountDelegations
);

const getPaginatedAccountTokens = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  NFTTokenResult,
  { hash: string; includes?: string; is_burned?: boolean }
>(`/accounts/:hash/nft-tokens`);
export const getPaginatedAccountTokensSubscription = baseSubscription(
  getPaginatedAccountTokens
);

const getPaginatedAccountDelegatorRewards = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  DelegatorRewardResult,
  { publicKey: string; includes?: string }
>(`/accounts/:publicKey/delegation-rewards`);
export const getPaginatedAccountDelegatorRewardsSubscription = baseSubscription(
  getPaginatedAccountDelegatorRewards
);

const getAccountsPublicKey = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  AccountBalanceInfoResult,
  { account_hash?: string; includes?: string }
>(`/accounts`);
export const getAccountsPublicKeySubscription =
  baseSubscription(getAccountsPublicKey);

const getAccountInfo = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<AccountInfoResult>,
  { accountHash: string; includes?: string }
>(`/accounts/:accountHash`);
export const getAccountInfoSubscription = baseSubscription(getAccountInfo);

const postAccounts = eventStoreApiClient(CSPR_LIVE_API_URL).createPaginatedPost<
  AccountInfoResult,
  { account_hashes?: string[]; includes?: string }
>(`/accounts`);
export const postAccountSubscription = baseSubscription(postAccounts);

const getAccountBalanceByPublicKey = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<AccountBalanceResult, { publicKey: string; includes?: string }>(
  `/accounts/:publicKey`
);
export const getAccountBalanceByPublicKeySubscription = baseSubscription(
  getAccountBalanceByPublicKey
);

/** CEP-18 **/

const getAccountCep18TokenList = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  DataResponse<FTBalanceTokenResult[]>,
  { accountHash: string; includes?: string }
>(`/accounts/:accountHash/ft-token-ownership`);
export const getAccountCep18TokenListSubscription = baseSubscription(
  getAccountCep18TokenList
);

const getContractPackageCep18TokenList = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  FTTokenOwnerResult,
  { contractPackageHash: string; includes?: string }
>(`/contract-packages/:contractPackageHash/ft-token-ownership`);
export const getContractPackageCep18TokenListSubscription = baseSubscription(
  getContractPackageCep18TokenList
);

const getPaginatedAccountTokensTxsList = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  FTTransactionResult,
  {
    accountHash: string;
    includes?: string;
  }
>(`/accounts/:accountHash/ft-token-actions`);
export const getPaginatedAccountTokensTxsListSubscription = baseSubscription(
  getPaginatedAccountTokensTxsList
);

const getPaginatedContractPackageTokensTxsList = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  FTTransactionResult,
  {
    contractPackageHash: string;
    includes?: string;
  }
>(`/contract-packages/:contractPackageHash/ft-token-actions`);
export const getPaginatedContractPackageTokensTxsListSubscription =
  baseSubscription(getPaginatedContractPackageTokensTxsList);

/** VALIDATOR **/

const getAuctionMetrics = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<ValidatorMetricsResult>,
  Record<string, never>
>(`/auction-metrics`);
export const getValidatorAuctionMetricsSubscription =
  baseSubscription(getAuctionMetrics);

const getPaginatedValidatorsData = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  ValidatorResult,
  { era_id: number; includes?: string; page_size?: number; is_active?: string }
>(`/validators`);

export const getPaginatedValidatorEraDataSubscription = pipe(
  baseSubscription(getPaginatedValidatorsData),
  map(MapPaginatedValidators)
);

const getPaginatedValidatorAuctionData = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  ValidatorResult,
  { era_id: number; includes?: string; page_size?: number; is_active?: string }
>(`/bidders`);
export const getPaginatedValidatorAuctionDataSubscription = pipe(
  baseSubscription(getPaginatedValidatorAuctionData),
  map(MapPaginatedValidators)
);

const getPaginatedValidatorBlocks = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<BlockResult, { publicKeyHex: string }>(
  `/validators/:publicKeyHex/blocks`
);
export const getPaginatedValidatorBlocksSubscription = pipe(
  baseSubscription(getPaginatedValidatorBlocks),
  map(MapPaginatedBlocks)
);

const getPaginatedValidatorRewards = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  ValidatorRewardResult,
  { publicKeyHex: string; includes?: string }
>(`/validators/:publicKeyHex/era-rewards`);
export const getPaginatedValidatorRewardsSubscription = baseSubscription(
  getPaginatedValidatorRewards
);

const getValidatorsTotalRewards = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<DataResponse<TotalRewardsResult>, { publicKey: string }>(
  `/validators/:publicKey/total-rewards`
);
export const getValidatorsTotalRewardsSubscription = baseSubscription(
  getValidatorsTotalRewards
);

const getValidatorsTotalDelegatorRewards = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<DataResponse<TotalRewardsResult>, { publicKey: string }>(
  `/validators/:publicKey/total-delegator-rewards`
);
export const getValidatorsTotalDelegatorRewardsSubscription = baseSubscription(
  getValidatorsTotalDelegatorRewards
);

const getValidatorsDelegations = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<DelegatorResult, { publicKey: string; includes?: string }>(
  `/validators/:publicKey/delegations`
);
export const getValidatorsDelegationsSubscription = baseSubscription(
  getValidatorsDelegations
);

/** BIDDER **/

const getBidderDetailsData = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<ValidatorResult> | null,
  { publicKey: string; era_id: number; includes?: string; is_active?: string }
>(`/bidders/:publicKey`);

export const getBidderDetailsDataSubscription = pipe(
  baseSubscription(getBidderDetailsData),
  map(MapDataResponseValidators)
);

/** DELEGATOR **/

const getDelegatorsTotalRewards = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<DataResponse<TotalRewardsResult>, { publicKey: string }>(
  `/accounts/:publicKey/total-delegation-rewards`
);
export const getDelegatorsTotalRewardsSubscription = baseSubscription(
  getDelegatorsTotalRewards
);

/** CURRECY RATES */

const getCurrencyRates = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<CurrencyRateResult>,
  { currencyCode: string }
>(`/rates/:currencyCode/amount`);
export const getCurrencyRateSubscription = baseSubscription(getCurrencyRates);

/** UNISWAP PAIRS */

const getFriendlyMarketTokenPair = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<
  DataResponse<FriendlyMarketTokenPairResult>,
  { contract_package_hash: string }
>(`/uniswap-v2-pairs/:contract_package_hash`);
export const getFriendlyMarketTokenPairSubscription = baseSubscription(
  getFriendlyMarketTokenPair
);

/** SUPPLY */

const getSupply = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<SupplyResult>,
  Record<string, never>
>(`/supply?is_cached=1`);
export const getSupplySubscription = baseSubscription(getSupply);

/** STATUS */

const getStatus = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<StatusResult>,
  Record<string, never>
>(`/network-state`);
export const getStatusSubscription = baseSubscription(getStatus);

/** PROXY
 * Those methods are proxies for RPC calls and returning cached data.
 * **/

export const rpcStateGetItem = eventStoreApiClient(RPC_URL).createRpcGet<
  RpcResponse<{ stored_value: any }>,
  {
    state_root_hash: string;
    key: string;
  }
>(`/state_get_item`);

export const rpcStateGetBalance = eventStoreApiClient(RPC_URL).createRpcGet<
  RpcResponse<{ balance_value: string }>,
  { state_root_hash: string; purse_uref: string }
>(`/state_get_balance`);

/** CONTRACT */
const getPaginatedContractPackages = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<DeployContractPackageResult, { includes?: string }>(
  `/contract-packages`
);
export const getPaginatedContractPackagesSubscription = baseSubscription(
  getPaginatedContractPackages
);

const getContractPackageInfo = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<DeployContractPackageResult> | null,
  { contract_package_hash: string; includes?: string }
>(`/contract-packages/:contract_package_hash`);
export const getContractPackageInfoSubscription = baseSubscription(
  getContractPackageInfo
);

const getPaginatedContractPackageTokens = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  NFTTokenResult,
  { contract_package_hash: string; includes?: string; is_burned?: boolean }
>(`/contract-packages/:contract_package_hash/nft-tokens`);

export const getPaginatedContractPackageTokensSubscription = baseSubscription(
  getPaginatedContractPackageTokens
);

const useGetContractNftToken = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<NFTTokenResult> & { metadata: NFTTokenMetadataEntry[] },
  { contract_package_hash: string; token_id: string; includes?: string }
>(`/contract-packages/:contract_package_hash/nft-tokens/:token_id`);
export const useGetContractNftTokenSubscription = baseSubscription(
  useGetContractNftToken
);

const useGetPaginatedNftTokenActivity = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  NFTTokenActivityResult,
  { contract_package_hash: string; token_id: string; includes?: string }
>(
  `/contract-packages/:contract_package_hash/nft-tokens/:token_id/nft-token-actions`
);
export const getPaginatedNftTokenActivitySubscription = baseSubscription(
  useGetPaginatedNftTokenActivity
);

const getContractInfo = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<ContractResult> | null,
  { contract_hash: string; includes?: string }
>(`/contracts/:contract_hash`);
export const getContractInfoSubscription = baseSubscription(getContractInfo);

const getPaginatedContractPackageNftOwners = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  NFTTokenOwnerResult,
  { contract_package_hash: string; includes?: string }
>(`/contract-packages/:contract_package_hash/nft-token-ownership`);

export const getPaginatedContractPackageNftOwnersSubscriptions =
  baseSubscription(getPaginatedContractPackageNftOwners);

/** CURRENCY */
const getAvailableCurrencyList = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<DataResponse<CurrencyFiatResult[]>, Record<string, never>>(
  `/currencies`
);
export const getAvailableCurrencyListSubscription = baseSubscription(
  getAvailableCurrencyList
);

/** ACTIVITY EXPORT */

const getPaginatedAccountTokensByContractPackageHash = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  ActivityExportEntitlementNFTEntryType,
  { accountHash: string; contractPackageHash: string; includes?: string }
>(
  `/accounts/:accountHash/nft-tokens?contract_package_hash=:contractPackageHash`
);
export const getPaginatedAccountTokensByContractPackageHashSubscription = pipe(
  baseSubscription(getPaginatedAccountTokensByContractPackageHash),
  map(MapTokensMetadataAsList),
  map(MapMetadata)
);

const getPaginatedActivityExportRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<ActivityExportRecentRequest, { publicKey: string }>(
  `/accounts/:publicKey/activity-requests`
);
export const getPaginatedActivityExportRequestSubscription = baseSubscription(
  getPaginatedActivityExportRequest
);

const postActivityExportRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPost<
  DataResponse<ActivityExportRequestResult>,
  ActivityExportRequestType,
  { publicKey: string }
>(`/accounts/:publicKey/activity-requests`);
export const postActivityExportRequestSubscription = postBaseSubscription(
  postActivityExportRequest
);

const createActivityExportDeployRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPost<
  RawDeployJSON,
  {
    account_public_key: string;
    years: number[];
  },
  {
    gas_price_tolerance: number;
  }
>(`/create-account-export-entitlement-deploy`);
export const createActivityExportDeployRequestSubscription =
  postBaseSubscription(createActivityExportDeployRequest);

const submitActivityExportDeployRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPost<
  DataResponse<ActivityExportRequestResult>,
  {
    deploy: any;
  },
  Record<string, never>
>(`/submit-account-export-entitlement-deploy`);
export const submitActivityExportDeployRequestSubscription =
  postBaseSubscription(submitActivityExportDeployRequest);

const getAccountActivityDeployStatuses = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<
  DataResponse<ActivityExportDeployStatusesResult>,
  Record<string, never>
>(`/account-activity-export-statuses`);
export const getAccountActivityDeployStatusesSubscription = baseSubscription(
  getAccountActivityDeployStatuses
);

/** ANALYTICS */

const getNetworkStats = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<NetworkStats> | null,
  Record<string, never>
>(`/network-stats`);
export const getNetworkStatsSubscription = baseSubscription(getNetworkStats);

const getRatesHistory = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  any,
  { from: string; to: string; currency_id: number; order_direction: string }
>(`/currencies/:currency_id/daily-rates`);
export const getRatesHistorySubscription = baseSubscription(getRatesHistory);

/** MULTISIG 1.5 */

const submitMultisigDeployRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPost<
  DataResponse,
  {
    recaptcha_client_token: string;
    deploy: any;
  },
  Record<string, never>
>(`/multisig-deploys`);
export const submitMultisigDeployRequestSubscription = postBaseSubscription(
  submitMultisigDeployRequest
);

const addSignatureToMultisigDeployRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPost<
  DataResponse,
  {
    signer: string;
    signature: string;
  },
  { transaction_hash: string }
>(`/multisig-deploys/:transaction_hash/approvals`);
export const addSignatureToMultisigDeployRequestSubscription =
  postBaseSubscription(addSignatureToMultisigDeployRequest);

const getMultisigDeployRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<DataResponse<RawDeployJSON>, { deploy_hash: string }>(
  `/multisig-deploys/:transaction_hash`
);
export const getMultisigDeployRequestSubscription = baseSubscription(
  getMultisigDeployRequest
);

/** MULTISIG 2.0 */

const submitMultisigTransactionRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPost<
  DataResponse,
  {
    recaptcha_client_token: string;
    transaction: any;
  },
  Record<string, never>
>(`/multisig-transactions`);
export const submitMultisigTransactionRequestSubscription =
  postBaseSubscription(submitMultisigTransactionRequest);

const addSignatureToMultisigTransactionRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPost<
  DataResponse,
  {
    signer: string;
    signature: string;
  },
  { transaction_hash: string }
>(`/multisig-transactions/:transaction_hash/approvals`);
export const addSignatureToMultisigTransactionRequestSubscription =
  postBaseSubscription(addSignatureToMultisigTransactionRequest);

const getMultisigTransactionRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<DataResponse<RawTransactionJSON>, { transaction_hash: string }>(
  `/multisig-transactions/:transaction_hash`
);
export const getMultisigTransactionRequestSubscription = baseSubscription(
  getMultisigTransactionRequest
);

const getKeysToWhichAccountAssociated = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  AssociatedKeyRelatedToAccount,
  { accountHash: string; page_size?: number; includes?: string }
>(`/accounts/:accountHash/associated-to-accounts`);
export const getKeysToWhichAccountAssociatedSubscription = baseSubscription(
  getKeysToWhichAccountAssociated
);

/** ADS */
const adsContactRequest = eventStoreApiClient(CSPR_LIVE_API_URL).createPost<
  DataResponse,
  {
    company_or_project_name: string;
    company_or_project_url: string;
    contact_email: string;
    contact_name: string;
    message: string;
    recaptcha_client_token: string;
  },
  Record<string, never>
>(`/ads-contact-requests`);
export const adsContactRequestSubscription =
  postBaseSubscription(adsContactRequest);

/** UREF */

const getUrefTransfersRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  TransferResult,
  { purseUref: string; includes?: string; limit?: number }
>(`/purses/:purseUref/transfers`);
export const getUrefTransfersRequestSubscription = baseSubscription(
  getUrefTransfersRequest
);

const getUrefDelegationsRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  URefDelegationResult,
  { urefKey: string; includes?: string }
>(`/urefs/:urefKey/delegations`);
export const getPaginatedUrefDelegationsRequestSubscription = baseSubscription(
  getUrefDelegationsRequest
);

const getUrefRewardsRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  DelegatorRewardResult,
  { urefKey: string; includes?: string }
>(`/urefs/:urefKey/delegation-rewards`);
export const getPaginatedUrefRewardsRequestSubscription = baseSubscription(
  getUrefRewardsRequest
);

const getUrefDelegatorsTotalRewards = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<DataResponse<string>, { uref_key: string }>(
  `/urefs/:uref_key/total-delegation-rewards`
);
export const getUrefDelegatorsTotalRewardsSubscription = baseSubscription(
  getUrefDelegatorsTotalRewards
);

const getUrefUndelegationBalance = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<DataResponse<any>, { uref_key: string; era_id: number }>(
  `/urefs/:uref_key/undelegation-balance/:era_id`
);
export const getUrefUndelegationBalanceSubscription = baseSubscription(
  getUrefUndelegationBalance
);

/** CSPR NAME */

const getCsprNameInfoRequest = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<CsprNameInfoResult>,
  { name: string; includes?: string }
>(`/cspr-name-resolutions/:name`);
export const getCsprNameInfoRequestSubscription = baseSubscription(
  getCsprNameInfoRequest
);
